import React, { createContext, useContext, useRef, useCallback, useMemo, useEffect, useState } from "react";
import useRestaurantWebSocket from "../../hooks/use-websocket";
import useMeshTransport, { isCWBrowser } from "../../hooks/useMeshTransport";
import { getPendingOrderNotifications, acknowledgeOrderNotification, salesHistoryDetails } from "../../services/orderServices";
import { isRecoveredOrderAlreadyHandled } from "../../utils/aggregatorOrderState";
import { getAccessToken } from "../../services/authInterceptor";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import { tillTakesOnlineOrders, tillNeedsLiveSocket } from "../../utils/onlineOrderGate";

const WebSocketContext = createContext(null);

// How often the POS asks the database for online orders it has not been told
// about. This is the worst-case lateness of a popup when every live transport
// has failed.
const PENDING_ORDER_POLL_MS = 30000;

// BUG-2373: current ofdStatus + server print flag of one recovered order, via
// the existing salesHistoryDetails service (resolves s_order_id or the
// aggregator external id). Returns { ofdStatus, print }, or undefined when the
// lookup failed -- which callers must treat as "unknown", never as "handled".
const fetchRecoveredOrderState = async (orderId) => {
  if (!orderId) return undefined;
  try {
    const response = await salesHistoryDetails({
      query: `query { salesHistoryDetails(orderId: "${orderId}") { sOrderID ofdStatus print } }`,
    });
    const row = response?.data?.data?.salesHistoryDetails?.[0];
    if (!row) return undefined;
    return { ofdStatus: row.ofdStatus ?? null, print: row.print ?? null };
  } catch (err) {
    console.error("[WebSocketProvider] Recovered order status lookup failed:", err);
    return undefined;
  }
};

const WebSocketProvider = ({ children, url, bunitId, tillId, tillLayout, enabled, debug }) => {
  const useMesh = isCWBrowser();
  const rawListenersRef = useRef(new Set());
  const [meshAutoStarted, setMeshAutoStarted] = useState(false);

  // Online-order gate (utils/onlineOrderGate.js): a till whose posConfig has
  // enableAggregator other than "Y" gets no pending-order poll, and on a retail
  // layout no server websocket. Follows every posConfig write (login, each sync,
  // rehydration). No config yet = open. Mesh is a LAN transport (no server
  // call) and stays as it was.
  const takesOnlineOrders = usePOSConfigStore((s) => tillTakesOnlineOrders(s.posConfig));
  const serverSocketEnabled = enabled && tillNeedsLiveSocket({ tillLayout, takesOnlineOrders });

  const addRawMessageListener = useCallback((listener) => {
    rawListenersRef.current.add(listener);
    return () => rawListenersRef.current.delete(listener);
  }, []);

  const removeRawMessageListener = useCallback((listener) => {
    rawListenersRef.current.delete(listener);
  }, []);

  const handleMessage = useCallback((message) => {
    rawListenersRef.current.forEach((listener) => {
      try {
        listener(message);
      } catch (err) {
        console.error("[WebSocketProvider] Listener error:", err);
      }
    });
  }, []);

  // Auto-elect mesh role when running inside CW Browser (desktop only — mobile has no autoJoinOrHost).
  // The shell owns the decision (persisted role → reconnect-to-remembered-host →
  // LAN discovery → self-promote-to-host): see meshPlugin.js's autoJoinOrHost.
  // This used to unconditionally call mesh.startHost() here, which made every
  // device its own isolated host — orders placed on one till never reached
  // another's screen because there was never any attempt to discover or join
  // an existing host first.
  useEffect(() => {
    if (!useMesh || !enabled || meshAutoStarted) return;

    const autoStart = async () => {
      try {
        const mesh = window.electronAPI.mesh;

        if (typeof mesh.autoJoinOrHost === "function") {
          console.debug("[WebSocketProvider] CW Browser detected — resolving mesh role...");
          const status = await mesh.autoJoinOrHost();
          console.debug("[WebSocketProvider] Mesh role resolved:", status.mode);
        } else {
          const status = await mesh.getStatus();
          console.debug("[WebSocketProvider] Mesh mode:", status.mode);
        }
        setMeshAutoStarted(true);
      } catch (err) {
        setMeshAutoStarted(true);
        console.error("[WebSocketProvider] Mesh auto-election failed:", err);
      }
    };

    autoStart();
  }, [useMesh, enabled, meshAutoStarted]);

  const meshTransport = useMeshTransport({
    bunitId,
    tillId,
    enabled: useMesh && enabled,
    debug,
    onMessage: handleMessage,
  });

  const wsTransport = useRestaurantWebSocket({
    url,
    bunitId,
    tillId,
    enabled: !useMesh && serverSocketEnabled,
    debug,
    onMessage: handleMessage,
  });

  const transport = useMesh ? meshTransport : wsTransport;

  // ---------------------------------------------------------------------
  // Database catch-up for online orders.
  //
  // Lives HERE, above the mesh/websocket choice, because it is the one part
  // of this that must not depend on a transport at all. Putting it inside
  // useRestaurantWebSocket meant it was dead on every CW Browser till: those
  // run mesh, so that hook receives enabled:false and never starts. The
  // failure it exists for - no popup while orders book normally - is exactly
  // when the live transport cannot be trusted to tell us anything.
  //
  // Recovered orders are queued behind whatever the active transport is
  // showing and surface one at a time (see visibleOrders below), so the popup
  // renders through the same path either way and App.js needs no changes.
  // ---------------------------------------------------------------------
  const [recoveredOrders, setRecoveredOrders] = useState([]);
  const recoveredOrdersRef = useRef([]);
  recoveredOrdersRef.current = recoveredOrders;
  const transportVisibleRef = useRef([]);
  transportVisibleRef.current = transport.visibleOrders || [];
  const seenOrderIdsRef = useRef(new Set());

  // Dismissing the popup is the operator confirming they have seen the order,
  // so it also retires it server-side. Without this the poll re-offers the
  // same order every 30s forever on every other device of the till.
  // Fire-and-forget: acknowledging is idempotent and the popup must close
  // whether or not the call succeeds.
  const acknowledgeOrder = useCallback(
    (orderId) => {
      if (!tillId || !orderId) return;
      Promise.resolve(acknowledgeOrderNotification(tillId, orderId)).catch((err) =>
        console.error("[WebSocketProvider] Acknowledge failed:", err)
      );
    },
    [tillId]
  );

  // Anything the live transport already showed must not be offered again by
  // the poll. Registering them here keeps one popup per order regardless of
  // which path won the race.
  useEffect(() => {
    (transport.visibleOrders || []).forEach((o) => {
      const id = o?.data?.orderId || o?.data?.externalId;
      if (id) seenOrderIdsRef.current.add(id);
    });
  }, [transport.visibleOrders]);

  const pollPendingOrders = useCallback(async () => {
    // tillId survives logout by design (till binding outlives the user
    // session — see useAuthStore.logout()), so this effect stays mounted and
    // ticking on /sign-in after logout. Without this check it fires an
    // unauthenticated GraphQL call every 30s, which 401s, which forces a
    // refresh attempt with no refreshToken, which hard-redirects to /sign-in
    // even when already there — a self-inflicted reload loop.
    if (!tillId || !getAccessToken()) return;
    // Read at the tick, not from the render: the config can change between ticks.
    if (!tillTakesOnlineOrders(usePOSConfigStore.getState().posConfig)) return;
    try {
      const response = await getPendingOrderNotifications(tillId, bunitId, 20);
      const pending = response?.data?.data?.getPendingOrderNotifications;
      if (!Array.isArray(pending) || pending.length === 0) return;

      // Waiting more than two minutes means the live path is down and this
      // poll is carrying the popup on its own - worth saying out loud.
      const stale = pending.filter((p) => (p.ageSeconds || 0) > 120);
      if (stale.length > 0) {
        console.warn(
          `[WebSocketProvider] ${stale.length} online order(s) recovered by poll, not by ${
            useMesh ? "mesh" : "websocket"
          } - check transport health`
        );
      }

      const candidates = pending.filter((o) => {
        const id = o.sOrderId || o.externalId || o.orderNo;
        if (!id || seenOrderIdsRef.current.has(id)) return false;
        seenOrderIdsRef.current.add(id);
        return true;
      });

      // BUG-2373: "not acknowledged" is not the same as "not handled". Orders
      // accepted from the Aggregator screen (or on another till) never
      // acknowledged their notification, so after every reload/login -- which
      // empties seenOrderIdsRef -- the poll handed back up to 20 old orders at
      // once, each popup auto-accepted 30s later and every one printed again
      // (GPR Miyapur E1, Sep: 338 orders, all CO/DI/FR/CC except 6 at AK).
      // Ask s_order first and retire, without a popup, anything already past
      // acceptance, or at AK with the print already confirmed server-side.
      // AK alone is NOT enough: UrbanPiper itself moves most orders to AK
      // before the POS touches them, so AK without print="Y" can still be an
      // order this till never printed. A failed lookup (undefined) keeps the
      // old behaviour and offers the popup -- a missed order must never be hidden.
      const statuses = await Promise.all(candidates.map((o) => fetchRecoveredOrderState(o.sOrderId || o.externalId)));
      const fresh = [];
      candidates.forEach((o, i) => {
        if (isRecoveredOrderAlreadyHandled(statuses[i])) {
          console.debug("[WebSocketProvider] Poll skipped already-handled order:", o.sOrderId, statuses[i]);
          acknowledgeOrder(o.sOrderId);
          return;
        }
        fresh.push({
          data: {
            orderId: o.sOrderId,
            orderNo: o.orderNo,
            externalId: o.externalId,
            tillId: o.tillId,
            storeId: o.storeId,
            recoveredByPoll: true,
          },
        });
      });

      if (fresh.length > 0) {
        console.debug("[WebSocketProvider] Orders recovered by poll:", fresh.length);
        setRecoveredOrders((prev) => [...prev, ...fresh]);
      }
    } catch (err) {
      // A failed poll is not fatal - the next one is 30s away.
      console.error("[WebSocketProvider] Pending order poll failed:", err);
    }
  }, [tillId, bunitId, useMesh, acknowledgeOrder]);

  useEffect(() => {
    if (!tillId) return;
    pollPendingOrders();
    const timer = setInterval(pollPendingOrders, PENDING_ORDER_POLL_MS);
    return () => clearInterval(timer);
  }, [tillId, pollPendingOrders]);

  // BUG-2373: one popup at a time, same as the live transports
  // (MAX_VISIBLE_NOTIFICATIONS = 1). Merging the whole recovered list in here
  // (e8d037d3) rendered up to 20 popups at once, each with its own 30s
  // auto-accept timer, so they all accepted and printed in the same second.
  // Live orders go first; recovered ones wait their turn.
  const visibleOrders = useMemo(() => {
    const live = transport.visibleOrders || [];
    if (live.length > 0) return live;
    return recoveredOrders.slice(0, 1);
  }, [transport.visibleOrders, recoveredOrders]);

  const removeVisibleOrder = useCallback(
    (orderId) => {
      acknowledgeOrder(orderId);
      setRecoveredOrders((prev) => prev.filter((o) => o?.data?.orderId !== orderId));
      transport.removeVisibleOrder?.(orderId);
    },
    [acknowledgeOrder, transport.removeVisibleOrder]
  );

  // Fired when the order is accepted somewhere else (order:update Acknowledged).
  // BUG-2373: used to acknowledge only recovered orders -- a live-transport
  // popup closed this way left its notification unacknowledged, and the poll
  // offered that already-accepted order again after the next reload.
  const removeVisibleOrderByExternalId = useCallback(
    (externalId) => {
      if (!externalId) return;
      const acked = new Set();
      [...recoveredOrdersRef.current, ...transportVisibleRef.current].forEach((o) => {
        const orderId = o?.data?.orderId;
        if (o?.data?.externalId === externalId && orderId && !acked.has(orderId)) {
          acked.add(orderId);
          acknowledgeOrder(orderId);
        }
      });
      setRecoveredOrders((prev) => prev.filter((o) => o?.data?.externalId !== externalId));
      transport.removeVisibleOrderByExternalId?.(externalId);
    },
    [acknowledgeOrder, transport.removeVisibleOrderByExternalId]
  );

  const value = useMemo(
    () => ({
      connected: transport.connected,
      reconnecting: transport.reconnecting,
      lastMessage: transport.lastMessage,
      // Merged: live transport orders plus anything the database poll
      // recovered. Consumers see one list and cannot tell which path a given
      // order arrived by.
      visibleOrders,
      orderUpdateFlag: transport.orderUpdateFlag,
      subscribe: transport.subscribe,
      // Wrapped so dismissing also acknowledges server-side and clears the
      // recovered list, not just the transport's own queue.
      removeVisibleOrder,
      removeVisibleOrderByExternalId,
      // BUG-2373: lets screens that accept orders without a popup (Aggregator)
      // retire the notification too, under the same till_ref the poll uses.
      acknowledgeOrder,
      sendMessage: transport.sendMessage,
      connect: transport.connect,
      disconnect: transport.disconnect,
      reconnect: transport.reconnect,
      addRawMessageListener,
      removeRawMessageListener,
      isMesh: useMesh,
      sendMeshEvent: useMesh ? meshTransport.sendMeshEvent : null,
      transportMode: useMesh ? "mesh" : "websocket",
    }),
    [
      transport.connected,
      transport.reconnecting,
      transport.lastMessage,
      visibleOrders,
      transport.orderUpdateFlag,
      transport.subscribe,
      removeVisibleOrder,
      removeVisibleOrderByExternalId,
      acknowledgeOrder,
      transport.sendMessage,
      transport.connect,
      transport.disconnect,
      transport.reconnect,
      addRawMessageListener,
      removeRawMessageListener,
      useMesh,
      meshTransport.sendMeshEvent,
    ]
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return ctx;
};

export { WebSocketProvider, useWebSocket };
